export type Maybe<T> = T | undefined;

/**
 * A type guard that checks if an object's `type` property is one of the provided values.
 * This function narrows the type of the object for the TypeScript compiler.
 *
 * @param t The object to check, which must have a `type` property (a discriminated union).
 * @param args A list of `type` strings to check against.
 * @returns A boolean indicating if the object's type matches, and narrows the object's type if true.
 */
export function isType<T extends { type: string }, K extends T["type"]>(
  t?: T,
  ...args: K[]
): t is Extract<T, { type: K }> {
  if (!t) return false;
  return args.some((a) => a === t.type);
}

/**
 * A type guard that checks if an object's `type` property is NOT one of the provided values.
 * This function narrows the type of the object to the remaining types in the union.
 *
 * @param t The object to check, which must have a `type` property (a discriminated union).
 * @param args A list of `type` strings to exclude.
 * @returns A boolean indicating if the object's type is not one of the provided types,
 *          and narrows the object's type if true.
 */
export function isNotType<T extends { type: string }, K extends T["type"]>(
  t: T,
  ...args: K[]
): t is Exclude<T, { type: K }> {
  // We can use !Array.prototype.includes for a slightly cleaner check.
  return !args.includes(t.type as K);
}
